import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from "@/api/apiClient";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const paramToken = searchParams.get('token');
    const verifyCalled = React.useRef(false);

    const [status, setStatus] = useState('verifying');
    const [message, setMessage] = useState('Vérification en cours...');

    useEffect(() => {
        if (!paramToken) {
            setStatus('error');
            setMessage('Lien de vérification invalide (token manquant).');
            return;
        }

        if (verifyCalled.current) return;
        verifyCalled.current = true;

        const verify = async () => {
            try {
                await apiClient.auth.verifyEmail(paramToken);
                setStatus('success');
                setMessage('Votre email a été vérifié avec succès !');
                toast.success('Email vérifié !');

                // Optional: Auto redirect after few seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } catch (error: any) {
                console.error("Verification failed", error);

                // If error suggests token is already used/invalid but we just clicked it, maybe show a softer message?
                // But for now, just showing the error is safer. 
                // However, handling the "Invalid token" if it was just verified is tricky without backend changes.
                // The Ref fix should solve the StrictMode double-call issue.

                setStatus('error');
                setMessage(error.response?.data?.error || 'Échec de la vérification. Le lien a peut-être expiré.');
            }
        };

        verify();
    }, [paramToken, navigate]);

    return (
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(228,14%,6%)]">
            <Card className="w-[400px]">
                <CardHeader className="text-center">
                    <CardTitle className="dark:text-white">Vérification Email</CardTitle>
                    <CardDescription className="dark:text-gray-400">Confirmation de votre compte</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-4 py-6">
                    {status === 'verifying' && (
                        <>
                            <Loader2 className="h-12 w-12 text-amber-500 animate-spin" />
                            <p className="text-gray-600 dark:text-gray-400">Vérification en cours...</p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <CheckCircle2 className="h-12 w-12 text-green-500" />
                            <p className="text-green-700 dark:text-green-400 font-medium text-center">{message}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Redirection vers la connexion...</p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <XCircle className="h-12 w-12 text-red-500" />
                            <p className="text-red-700 dark:text-red-400 font-medium text-center">{message}</p>
                        </>
                    )}
                </CardContent>
                <CardFooter className="flex justify-center">
                    {status === 'error' && (
                        <Button onClick={() => navigate('/login')} variant="outline">
                            Retour à la connexion
                        </Button>
                    )}
                    {status === 'success' && (
                        <Button onClick={() => navigate('/login')} className="bg-green-600 hover:bg-green-700">
                            Se connecter maintenant
                        </Button>
                    )}
                </CardFooter>
            </Card>
        </div>
    );
};

export default VerifyEmail;
